
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useChecklistItems } from './useChecklistItems';

export const useNoteChecklistItems = (noteId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [virtualCardId, setVirtualCardId] = useState<string | null>(null);

  // Get or create virtual card for this note
  const { data: virtualCard, isLoading: isLoadingVirtualCard } = useQuery({
    queryKey: ['virtualCard', noteId],
    queryFn: async () => {
      if (!noteId) return null;

      // Check if virtual card already exists
      const { data: existingCard, error: fetchError } = await supabase
        .from('cards')
        .select('id, column_id')
        .eq('title', `__NOTE_VIRTUAL_CARD_${noteId}`)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existingCard) {
        return existingCard;
      }

      // Create virtual card if it doesn't exist
      // First, we need to find a column to put it in (we'll use the first available column)
      const { data: columns, error: columnsError } = await supabase
        .from('columns')
        .select('id')
        .limit(1)
        .single();

      if (columnsError) {
        // If no columns exist, we can't create virtual cards yet
        return null;
      }

      const { data: newCard, error: createError } = await supabase
        .from('cards')
        .insert({
          title: `__NOTE_VIRTUAL_CARD_${noteId}`,
          column_id: columns.id,
          position: 999999, // Put at end to avoid conflicts
          description: `Virtual card for note checklists - ${noteId}`,
          archived: true, // Mark as archived so it doesn't appear in normal board view
        })
        .select('id, column_id')
        .single();

      if (createError) throw createError;

      return newCard;
    },
    enabled: !!noteId,
  });

  // Update virtual card ID when it changes
  useEffect(() => {
    if (virtualCard?.id) {
      setVirtualCardId(virtualCard.id);
    }
  }, [virtualCard]);

  // Use existing checklist items hook with virtual card ID
  const checklistHook = useChecklistItems(virtualCardId || '');

  // Clean up virtual card when all checklists are removed
  const cleanupVirtualCard = useMutation({
    mutationFn: async () => {
      if (!virtualCardId) return;

      const { error } = await supabase
        .from('cards')
        .delete()
        .eq('id', virtualCardId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtualCard', noteId] });
      setVirtualCardId(null);
    },
    onError: (error: any) => {
      console.error('Error cleaning up virtual card:', error);
      toast({
        title: "Error",
        description: "Failed to clean up virtual card",
        variant: "destructive",
      });
    },
  });

  // Enhanced create function that ensures virtual card exists
  const createChecklistItem = async (itemData: any) => {
    if (!virtualCardId) {
      toast({
        title: "Error", 
        description: "Virtual card not ready",
        variant: "destructive"
      });
      return;
    }

    return checklistHook.createChecklistItem({
      ...itemData,
      card_id: virtualCardId,
    });
  };

  // Enhanced create checklist function with proper naming
  const createChecklistWithName = async (checklistName: string) => {
    if (!virtualCardId || !checklistName.trim()) return;

    const checklistId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    
    // Store checklist name in the first item's text with a special prefix
    return checklistHook.createChecklistItem({
      card_id: virtualCardId,
      checklist_id: checklistId,
      text: `__CHECKLIST_NAME__${checklistName}__ADD_FIRST_TASK__`,
      position: 0,
    });
  };

  return {
    ...checklistHook,
    isLoadingVirtualCard,
    virtualCardId,
    createChecklistItem,
    createChecklistWithName,
    cleanupVirtualCard: cleanupVirtualCard.mutate,
    isCleaningUp: cleanupVirtualCard.isPending,
  };
};
